import React, { useState, useRef, useEffect, useMemo } from "react";
import { photoGalleryApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import { compressImage } from "../../utils/imageCompressor";
import { getImageUrl, handleImageError } from "../../utils/imageUrl";
import "./PhotoGallery.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = ["Campus", "Events", "Sports", "Cultural", "Academic", "Infrastructure", "Other"];

const BLANK_PHOTO_FORM = { 
  _id: null, Photo_Title: "", Category: "Campus", Album_ID: "", 
  Image_URL: "", Caption: "", Date: new Date().toISOString().slice(0, 10), 
  Mark_as_Featured: false, Visible_on_Website: true 
};

const BLANK_ALBUM_FORM = { 
  _id: null, Album_Name: "", Description: "", Cover_Photo_URL: "" 
};

const PhotoGallery = () => {
  const [albums, setAlbums]             = useState([]);
  const [photos, setPhotos]             = useState([]);
  const [loading, setLoading]           = useState(false);

  const [openAlbumId, setOpenAlbumId]   = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch]             = useState("");
  const [mainView, setMainView]         = useState("album"); // Default to album view for "Impressive" start

  const [photoModal, setPhotoModal]     = useState("");
  const [photoForm, setPhotoForm]       = useState(BLANK_PHOTO_FORM);
  const [albumModal, setAlbumModal]     = useState("");
  const [albumForm, setAlbumForm]       = useState(BLANK_ALBUM_FORM);

  const [confirmState, setConfirmState] = useState({ type: "", id: null });
  const [lightbox, setLightbox]         = useState(null);
  const [dropdown, setDropdown]         = useState({ type: "", id: null, top: 0, left: 0 });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl]     = useState(null);
  const dropdownRef                     = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [albRes, phoRes] = await Promise.all([
        apiClient(`${photoGalleryApi}/albums`),
        apiClient(`${photoGalleryApi}/photos`)
      ]);
      const albResult = await albRes.json();
      const phoResult = await phoRes.json();
      if (albRes.ok) setAlbums(albResult.data || []);
      if (phoRes.ok) setPhotos(phoResult.data || []);
    } catch (error) {
      console.error("Error fetching gallery data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdown({ type: "", id: null, top: 0, left: 0 }); };
    const onClose = () => setDropdown({ type: "", id: null, top: 0, left: 0 });
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
    };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filteredPhotos = useMemo(() => {
    return photos.filter((p) => {
      const matchAlbum    = !openAlbumId || (p.Album_ID?._id === openAlbumId) || (p.Album_ID === openAlbumId);
      const matchCategory = activeCategory === "All" || p.Category === activeCategory;
      const matchSearch   = !search || (p.Photo_Title || "").toLowerCase().includes(search.toLowerCase());
      return matchAlbum && matchCategory && matchSearch;
    });
  }, [photos, openAlbumId, activeCategory, search]);

  const stats = useMemo(() => ({
    totalPhotos: photos.length,
    totalAlbums: albums.length,
    featured: photos.filter(p => p.Mark_as_Featured).length,
    hidden: photos.filter(p => !p.Visible_on_Website).length
  }), [photos, albums]);

  const categoryCounts = useMemo(() => {
    const counts = { All: photos.length };
    CATEGORIES.forEach(c => {
      counts[c] = photos.filter(p => p.Category === c).length;
    });
    return counts;
  }, [photos]);

  const currentAlbum = useMemo(() => {
    return albums.find(a => a._id === openAlbumId);
  }, [albums, openAlbumId]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const openDropdown = (e, type, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdown({ type, id, top: rect.bottom + 8, left: rect.right - 210 });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const compressed = await compressImage(file);
      setSelectedFile(compressed);
      setPreviewUrl(URL.createObjectURL(compressed));
    }
  };

  const removePhoto = (type) => {
    if (type === 'album') {
      setAlbumForm(p => ({ ...p, Cover_Photo_URL: "" }));
    } else {
      setPhotoForm(p => ({ ...p, Image_URL: "" }));
    }
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const saveAlbum = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = albumModal === "add" ? "POST" : "PUT";
      const url = albumModal === "add" ? `${photoGalleryApi}/albums` : `${photoGalleryApi}/albums/${albumForm._id}`;
      
      const formDataToSend = new FormData();
      Object.keys(albumForm).forEach(key => {
        if (key !== 'Cover_Photo_URL' && albumForm[key] !== null) {
          formDataToSend.append(key, albumForm[key]);
        }
      });

      if (selectedFile) {
        const compressed = await compressImage(selectedFile);
        formDataToSend.append('Cover_Photo_URL', compressed);
      } else if (albumForm.Cover_Photo_URL) {
        formDataToSend.append('Cover_Photo_URL', albumForm.Cover_Photo_URL);
      }

      const response = await apiFormDataClient(url, { method, body: formDataToSend });
      if (response.ok) {
        fetchData();
        setAlbumModal("");
      } else {
        const errJson = await response.json().catch(() => ({}));
        alert(`Failed to save album: ${errJson.message || response.statusText || 'Server error'}`);
      }
    } catch (error) {
      console.error("Error saving album:", error);
      alert(`Error saving album: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const savePhoto = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = photoModal === "add" ? "POST" : "PUT";
      const url = photoModal === "add" ? `${photoGalleryApi}/photos` : `${photoGalleryApi}/photos/${photoForm._id}`;
      
      const formDataToSend = new FormData();
      Object.keys(photoForm).forEach(key => {
        if (key !== 'Image_URL' && photoForm[key] !== null) {
          formDataToSend.append(key, photoForm[key]);
        }
      });

      if (selectedFile) {
        const compressed = await compressImage(selectedFile);
        formDataToSend.append('Image_URL', compressed);
      } else if (photoForm.Image_URL) {
        formDataToSend.append('Image_URL', photoForm.Image_URL);
      }

      const response = await apiFormDataClient(url, { method, body: formDataToSend });
      if (response.ok) {
        fetchData();
        setPhotoModal("");
      } else {
        const errJson = await response.json().catch(() => ({}));
        alert(`Failed to save photo: ${errJson.message || response.statusText || 'Server error'}`);
      }
    } catch (error) {
      console.error("Error saving photo:", error);
      alert(`Error saving photo: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const type = confirmState.type === "album" ? "albums" : "photos";
      const url = `${photoGalleryApi}/${type}/${confirmState.id}`;
      const response = await apiClient(url, { method: "DELETE" });
      if (response.ok) {
        fetchData();
        setConfirmState({ type: "", id: null });
        if (confirmState.type === "album" && openAlbumId === confirmState.id) setOpenAlbumId(null);
      }
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="pg-page">
      {/* ── Header ── */}
      <div className="pg-header">
        <div className="pg-header-left">
          <div className="pg-header-icon">🖼️</div>
          <div>
            <div className="pg-breadcrumb">
              <span>Media & Contact</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              {openAlbumId ? (
                <span className="pg-bc-link" onClick={() => {setOpenAlbumId(null); setMainView("album");}}>Photo Gallery</span>
              ) : (
                <span className="pg-bc-active">Photo Gallery</span>
              )}
              {openAlbumId && (
                <>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                  <span className="pg-bc-active">{currentAlbum?.Album_Name}</span>
                </>
              )}
            </div>
            <h1>Photo Gallery</h1>
            <p>{openAlbumId ? `Viewing photos from "${currentAlbum?.Album_Name}" album.` : "Curate and manage campus moments, events, and academic milestones."}</p>
          </div>
        </div>
        <div className="pg-header-actions">
           {!openAlbumId && (
             <button className="pg-btn-secondary" onClick={() => { setAlbumForm(BLANK_ALBUM_FORM); setSelectedFile(null); setPreviewUrl(null); setAlbumModal("add"); }}>
               <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
               New Album
             </button>
           )}
           <button className="pg-btn-primary" onClick={() => { setPhotoForm({ ...BLANK_PHOTO_FORM, Album_ID: openAlbumId || "" }); setSelectedFile(null); setPreviewUrl(null); setPhotoModal("add"); }}>
             <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
             Add Photo
           </button>
        </div>
      </div>

      {loading && <div className="loading-overlay">Syncing visual assets...</div>}

      {/* ── Stats Bar ── */}
      {!openAlbumId ? (
        <div className="pg-stats-row">
          <div className="pg-stat-card"><span className="pg-stat-icon">📸</span><div><strong>{stats.totalPhotos}</strong><p>Total Photos</p></div></div>
          <div className="pg-stat-card"><span className="pg-stat-icon">📂</span><div><strong>{stats.totalAlbums}</strong><p>Albums</p></div></div>
          <div className="pg-stat-card" style={{ borderTopColor: '#f59e0b' }}><span className="pg-stat-icon">★</span><div><strong>{stats.featured}</strong><p>Featured Items</p></div></div>
          <div className="pg-stat-card" style={{ borderTopColor: '#94a3b8' }}><span className="pg-stat-icon">🔒</span><div><strong>{stats.hidden}</strong><p>Hidden Assets</p></div></div>
        </div>
      ) : (
        <div className="pg-album-stat-bar">
          <span>Album: <strong>{currentAlbum?.Album_Name}</strong></span>
          <span>Photos: <strong>{photos.filter(p => (p.Album_ID?._id || p.Album_ID) === openAlbumId).length}</strong></span>
          <button className="pg-btn-light" style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: 12 }} onClick={() => setOpenAlbumId(null)}>← Back to All Albums</button>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="pg-filters">
        <div className="pg-filters-top">
          <div className="pg-search">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input type="text" placeholder="Search by title..." value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <button className="pg-clr-search" onClick={() => setSearch("")}>×</button>}
          </div>
          {!openAlbumId && (
            <div className="pg-view-toggle">
              <button className={`pg-view-btn ${mainView === 'album' ? 'active' : ''}`} onClick={() => setMainView('album')}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                Albums
              </button>
              <button className={`pg-view-btn ${mainView === 'grid' ? 'active' : ''}`} onClick={() => setMainView('grid')}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                All Photos
              </button>
            </div>
          )}
        </div>
        <div className="pg-cat-tabs">
          {["All", ...CATEGORIES].map(cat => (
            <button key={cat} className={`pg-cat-tab ${activeCategory === cat ? 'active' : ''}`} onClick={() => setActiveCategory(cat)}>
              {cat} <span className="pg-cat-count">{categoryCounts[cat] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Main View: Albums ── */}
      {mainView === "album" && !openAlbumId && (
        <div className="pg-albums-grid">
          <div className="pg-album-new-card" onClick={() => { setAlbumForm(BLANK_ALBUM_FORM); setSelectedFile(null); setPreviewUrl(null); setAlbumModal("add"); }}>
            <div className="pg-album-new-icon"><svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg></div>
            <span>Create New Album</span>
          </div>
          {albums.map(album => {
            const albumPhotos = photos.filter(p => (p.Album_ID?._id || p.Album_ID) === album._id);
            return (
              <div key={album._id} className="pg-album-card">
                <div className="pg-album-cover" onClick={() => { setOpenAlbumId(album._id); setMainView("grid"); }}>
                  <img src={getImageUrl(album.Cover_Photo_URL, photoGalleryApi)} onError={(e) => handleImageError(e, album.Album_Name)} alt={album.Album_Name} />
                  <div className="pg-album-cover-overlay"><span>VIEW ALBUM</span></div>
                  <div className="pg-album-photo-count">{albumPhotos.length} Photos</div>
                </div>
                <div className="pg-album-card-info">
                  <div className="pg-album-card-top">
                    <h3 onClick={() => { setOpenAlbumId(album._id); setMainView("grid"); }}>{album.Album_Name}</h3>
                    <button className="pg-dots" onClick={(e) => openDropdown(e, "album", album._id)}>⋮</button>
                  </div>
                  <p>{album.Description || "No description provided."}</p>
                  <div className="pg-album-card-actions">
                    <button className="pg-album-link" onClick={() => { setOpenAlbumId(album._id); setMainView("grid"); }}>Open</button>
                    <button className="pg-album-link add" onClick={() => { setPhotoForm({ ...BLANK_PHOTO_FORM, Album_ID: album._id }); setSelectedFile(null); setPreviewUrl(null); setPhotoModal("add"); }}>+ Add Photo</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Main View: Photo Grid ── */}
      {(mainView === "grid" || openAlbumId) && (
        <div className="pg-photo-grid">
          {filteredPhotos.map((photo, idx) => (
            <div key={photo._id} className={`pg-photo-card ${!photo.Visible_on_Website ? 'hidden-card' : ''}`}>
              <div className="pg-photo-img-wrap" onClick={() => setLightbox({ photos: filteredPhotos, index: idx })}>
                <img src={getImageUrl(photo.Image_URL, photoGalleryApi)} onError={(e) => handleImageError(e, photo.Photo_Title)} alt={photo.Photo_Title} />
                <div className="pg-photo-overlay"><svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg></div>
                <div className="pg-photo-badges">
                  {photo.Mark_as_Featured && <div className="pg-feat-dot" title="Featured Photo">⭐</div>}
                  {!photo.Visible_on_Website && <div className="pg-hidden-dot" title="Hidden from Website">🔒</div>}
                </div>
              </div>
              <div className="pg-photo-info">
                <div className="pg-photo-info-top">
                  <span className="pg-cat-pill" style={{ background: '#f1f5f9', color: '#475569' }}>{photo.Category}</span>
                  <button className="pg-dots" onClick={(e) => openDropdown(e, "photo", photo._id)}>⋮</button>
                </div>
                <h3>{photo.Photo_Title}</h3>
                <div className="pg-photo-meta">
                  <span>📅 {formatDate(photo.Date)}</span>
                  <span>📂 {photo.Album_ID?.Album_Name || "No Album"}</span>
                </div>
              </div>
            </div>
          ))}
          {filteredPhotos.length === 0 && (
            <div className="pg-empty" style={{ gridColumn: '1 / -1' }}>
              <div>🏜️</div>
              <h3>No photos found</h3>
              <p>Try adjusting your search or filters to see more results.</p>
              <button className="pg-btn-primary" onClick={() => { setSearch(""); setActiveCategory("All"); setOpenAlbumId(null); setMainView("album"); }}>Reset All Filters</button>
            </div>
          )}
        </div>
      )}

      {/* ── Dropdown Menu ── */}
      {dropdown.id && (
        <div ref={dropdownRef} className="pg-dropdown" style={{ position: "fixed", top: dropdown.top, left: dropdown.left, zIndex: 9999 }}>
          {dropdown.type === "album" ? (
            <>
              <button onClick={() => { setAlbumForm(albums.find(a => a._id === dropdown.id)); setAlbumModal("edit"); setDropdown({ type: "", id: null }); }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4L18.5 2.5z" /></svg>
                Edit Album
              </button>
              <div className="pg-dd-divider"></div>
              <button className="pg-dd-del" onClick={() => { setConfirmState({ type: "album", id: dropdown.id }); setDropdown({ type: "", id: null }); }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                Delete Album
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { 
                const p = photos.find(x => x._id === dropdown.id);
                setPhotoForm({ ...p, Date: p.Date ? new Date(p.Date).toISOString().split('T')[0] : "", Album_ID: p.Album_ID?._id || p.Album_ID }); 
                setPhotoModal("edit"); 
                setDropdown({ type: "", id: null });
              }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4L18.5 2.5z" /></svg>
                Edit Details
              </button>
              <div className="pg-dd-divider"></div>
              <button className="pg-dd-del" onClick={() => { setConfirmState({ type: "photo", id: dropdown.id }); setDropdown({ type: "", id: null }); }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                Remove Photo
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Album Modal ── */}
      {albumModal && (
        <div className="pg-overlay" onClick={() => setAlbumModal("")}>
          <div className="pg-form-modal small" onClick={e => e.stopPropagation()}>
            <div className="pg-modal-head">
              <div><h2>{albumModal === "add" ? "Create New" : "Edit"} Album</h2><p>Organize your photos into collections.</p></div>
              <button className="pg-modal-close" onClick={() => setAlbumModal("")}>×</button>
            </div>
            <form className="pg-form" onSubmit={saveAlbum}>
              <div className="pg-field full"><label>Album Name <span className="req">*</span></label><input value={albumForm.Album_Name || ""} onChange={(e) => setAlbumForm({...albumForm, Album_Name: e.target.value})} required placeholder="e.g. Annual Sports Meet 2024" /></div>
              <div className="pg-field full"><label>Description</label><textarea value={albumForm.Description || ""} onChange={(e) => setAlbumForm({...albumForm, Description: e.target.value})} placeholder="Describe the purpose or event of this album..." rows="3" /></div>
              <div className="pg-field full">
                <label>Album Cover Photo</label>
                <div className="pg-photo-upload">
                  {(previewUrl || albumForm.Cover_Photo_URL) ? (
                    <div className="pg-photo-preview">
                      <img src={previewUrl || getImageUrl(albumForm.Cover_Photo_URL, photoGalleryApi)} onError={(e) => handleImageError(e, "Preview")} alt="Preview" />
                      <div className="pg-photo-actions">
                        <label className="pg-change-btn">Change Cover<input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} /></label>
                        <button type="button" className="pg-remove-btn" onClick={() => removePhoto('album')}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <label className="pg-upload-placeholder">
                      <div className="pg-upload-icon"><svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg></div>
                      <span>Click to upload cover image</span>
                      <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                    </label>
                  )}
                </div>
              </div>
              <div className="pg-form-footer">
                <button type="button" className="pg-btn-light" onClick={() => setAlbumModal("")}>Cancel</button>
                <button type="submit" className="pg-btn-primary">Save Album</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Photo Modal ── */}
      {photoModal && (
        <div className="pg-overlay" onClick={() => setPhotoModal("")}>
          <div className="pg-form-modal" onClick={e => e.stopPropagation()}>
            <div className="pg-modal-head">
              <div><h2>{photoModal === "add" ? "Add New" : "Edit"} Photo</h2><p>Upload a photo and assign it to an album.</p></div>
              <button className="pg-modal-close" onClick={() => setPhotoModal("")}>×</button>
            </div>
            <form className="pg-form" onSubmit={savePhoto}>
              <div className="pg-field full"><label>Photo Title <span className="req">*</span></label><input value={photoForm.Photo_Title || ""} onChange={(e) => setPhotoForm({...photoForm, Photo_Title: e.target.value})} required placeholder="e.g. Prize Distribution Ceremony" /></div>
              <div className="pg-field-row">
                <div className="pg-field">
                  <label>Category</label>
                  <select value={photoForm.Category || "Campus"} onChange={(e) => setPhotoForm({...photoForm, Category: e.target.value})}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="pg-field">
                  <label>Assign to Album <span className="req">*</span></label>
                  <select value={photoForm.Album_ID || ""} onChange={(e) => setPhotoForm({...photoForm, Album_ID: e.target.value})} required>
                    <option value="">Select Album</option>
                    {albums.map(a => <option key={a._id} value={a._id}>{a.Album_Name}</option>)}
                  </select>
                </div>
              </div>
              <div className="pg-field full">
                <label>Photo Source <span className="req">*</span></label>
                <div className="pg-photo-upload">
                  {(previewUrl || photoForm.Image_URL) ? (
                    <div className="pg-photo-preview">
                      <img src={previewUrl || getImageUrl(photoForm.Image_URL, photoGalleryApi)} onError={(e) => handleImageError(e, "Preview")} alt="Preview" />
                      <div className="pg-photo-actions">
                        <label className="pg-change-btn">Change Photo<input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} /></label>
                        <button type="button" className="pg-remove-btn" onClick={() => removePhoto('photo')}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <label className="pg-upload-placeholder">
                      <div className="pg-upload-icon"><svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg></div>
                      <span>Select Photo to Upload</span>
                      <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                    </label>
                  )}
                </div>
              </div>
              <div className="pg-field full"><label>Caption / Alt Text</label><input value={photoForm.Caption || ""} onChange={(e) => setPhotoForm({...photoForm, Caption: e.target.value})} placeholder="Short description for accessibility..." /></div>
              <div className="pg-field-row">
                <div className="pg-field"><label>Date Captured</label><input type="date" value={photoForm.Date || ""} onChange={(e) => setPhotoForm({...photoForm, Date: e.target.value})} /></div>
                <div className="pg-toggle-row">
                  <label className="pg-toggle-item">
                    <input type="checkbox" checked={photoForm.Mark_as_Featured || false} onChange={(e) => setPhotoForm({...photoForm, Mark_as_Featured: e.target.checked})} />
                    <div className="pg-chk-box"></div>
                    <div><strong>Featured Content</strong><p>Show in highlights section on website.</p></div>
                  </label>
                  <label className="pg-toggle-item">
                    <input type="checkbox" checked={photoForm.Visible_on_Website || false} onChange={(e) => setPhotoForm({...photoForm, Visible_on_Website: e.target.checked})} />
                    <div className="pg-chk-box"></div>
                    <div><strong>Live on Website</strong><p>Publish this photo to the public gallery.</p></div>
                  </label>
                </div>
              </div>
              <div className="pg-form-footer">
                <button type="button" className="pg-btn-light" onClick={() => setPhotoModal("")}>Cancel</button>
                <button type="submit" className="pg-btn-primary">Save Photo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm Deletion ── */}
      {confirmState.id && (
        <div className="pg-overlay" onClick={() => setConfirmState({ type: "", id: null })}>
          <div className="pg-confirm" onClick={e => e.stopPropagation()}>
            <div className="pg-confirm-icon">🗑️</div>
            <h3>Delete {confirmState.type === 'album' ? 'Album' : 'Photo'}?</h3>
            <p>This action will permanently remove the {confirmState.type} from the gallery records. This cannot be undone.</p>
            <div className="pg-confirm-actions">
              <button className="pg-btn-light" onClick={() => setConfirmState({ type: "", id: null })}>Cancel</button>
              <button className="pg-btn-danger" onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox / Image Preview ── */}
      {lightbox && (
        <div className="pg-lightbox" onClick={() => setLightbox(null)}>
          <button className="pg-lb-close" onClick={() => setLightbox(null)}>×</button>
          <button className="pg-lb-nav prev" disabled={lightbox.index === 0} onClick={(e) => { e.stopPropagation(); setLightbox({...lightbox, index: lightbox.index - 1}); }}>‹</button>
          <button className="pg-lb-nav next" disabled={lightbox.index === lightbox.photos.length - 1} onClick={(e) => { e.stopPropagation(); setLightbox({...lightbox, index: lightbox.index + 1}); }}>›</button>
          
          <div className="pg-lightbox-inner" onClick={e => e.stopPropagation()}>
            <div className="pg-lb-img-wrap">
              <img src={lightbox.photos[lightbox.index].Image_URL} alt="Lightbox Preview" />
            </div>
            <div className="pg-lb-info">
              <div className="pg-lb-info-left">
                <h3>{lightbox.photos[lightbox.index].Photo_Title}</h3>
                <p>{lightbox.photos[lightbox.index].Caption || "No caption provided."}</p>
                <div className="pg-lb-meta">
                  <span className="pg-cat-pill" style={{ background: '#334155', color: '#fff' }}>{lightbox.photos[lightbox.index].Category}</span>
                  <span>📅 {formatDate(lightbox.photos[lightbox.index].Date)}</span>
                </div>
              </div>
              <div className="pg-lb-counter">IMAGE {lightbox.index + 1} OF {lightbox.photos.length}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;